import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, CheckCircle, AlertCircle, Search, Sparkles, FileText, ArrowRight, Zap } from "lucide-react";
import { Badge } from "../../ui/badge";

const reasoningSteps = [
  { text: "Gathering EOB details, claim data, and patient records in parallel...", status: "done", delay: 600 },
  { text: "Loaded EOB: Delta Dental of California — $0.00 paid, status: denied", status: "done", delay: 500 },
  { text: "EOB adjustments analyzed — 2 service lines", status: "done", delay: 400 },
  { text: "Denial: hard severity — CO-50 (non-covered service)", status: "alert", delay: 500 },
  { text: "Skill catalog loaded — 9 denial-specific skills available", status: "done", delay: 400 },
  { text: "1 clinical note loaded — perio charting attached", status: "done", delay: 400 },
  { text: "Perio exam loaded — Stage III, pockets 4-6mm, significant BOP", status: "done", delay: 500 },
  { text: "Benefits loaded — ACTIVE coverage, D4341 eligible", status: "done", delay: 400 },
  { text: "Claim history: 5 prior claims loaded — 0 prior appeals", status: "search", delay: 500 },
  { text: "Adjudication history loaded — 98% denial rate for this code/payer", status: "done", delay: 500 },
  { text: "Skill match: SRP Denial — Contradictory Perio Data Detection", status: "done", delay: 500 },
  { text: "Pockets ≥4mm + significant BOP → clinical necessity confirmed", status: "done", delay: 500 },
  { text: "Loaded challenge-my-reasoning — reviewer flagged important points", status: "challenge", delay: 600 },
  { text: "Loaded challenge-my-reasoning — reviewer approved", status: "done", delay: 500 },
  { text: "Recommendation: APPEAL_CLAIM — medium confidence", status: "done", delay: 400 },
  { text: "Appeal letter drafted with clinical evidence attached", status: "done", delay: 400 },
];

const DenialManagementSlide = () => {
  const [phase, setPhase] = useState("reasoning"); // reasoning, recommendation, complete
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const stepsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const cycle = async () => {
      while (!cancelled) {
        // Reset
        setPhase("reasoning");
        setVisibleSteps(0);
        setShowRecommendation(false);
        setShowComplete(false);
        await new Promise((r) => setTimeout(r, 1200));

        // Show reasoning steps one by one
        for (let i = 1; i <= reasoningSteps.length; i++) {
          if (cancelled) return;
          setVisibleSteps(i);
          await new Promise((r) => setTimeout(r, reasoningSteps[i - 1].delay));
        }

        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 800));

        // Show recommendation
        setPhase("recommendation");
        setShowRecommendation(true);
        await new Promise((r) => setTimeout(r, 3000));

        // Show complete
        if (cancelled) return;
        setPhase("complete");
        setShowComplete(true);
        await new Promise((r) => setTimeout(r, 5000));
      }
    };
    cycle();
    return () => { cancelled = true; };
  }, []);

  // Auto-scroll reasoning steps
  useEffect(() => {
    if (stepsRef.current) {
      stepsRef.current.scrollTop = stepsRef.current.scrollHeight;
    }
  }, [visibleSteps]);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <Shield className="w-4 h-4" />
              <span>Revenue Cycle AI</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              AI{" "}
              <span className="text-blue-600">Denial Management</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Sarah analyzes every denied claim by pulling EOB data, clinical notes, payer
              policies, and claim history — then reasons through the best course of action.
              One tap to submit an appeal, corrected claim, or write-off.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-2">
            {[
              "Multi-Step Reasoning",
              "Clinical Evidence",
              "Payer Policy Matching",
              "Auto-Appeal Generation",
              "Corrected Claims",
              "Self-Challenging AI",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">16+</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Data Points Analyzed
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">1-Tap</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Appeal Submission
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Denial Reasoning Interface */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header — Claim Info */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900 text-sm">Maria Garcia</h3>
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border border-red-200 text-[10px]">
                      Denied
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500">D4341 (SRP) x2 — $1,240 — Delta Dental of CA</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400">Submitted 3/22/2026</span>
              </div>
            </div>

            {/* Tabs */}
            <div className="h-10 border-b border-slate-100 flex items-center px-6 gap-4">
              {["Patient", "Procedures", "Provider", "Attachments", "EOB", "Visit Data"].map((tab, i) => (
                <span key={i} className="text-xs text-slate-400 font-medium">{tab}</span>
              ))}
              <span className="text-xs text-blue-600 font-bold border-b-2 border-blue-600 pb-2.5 -mb-[1px]">AI</span>
            </div>

            {/* AI Analysis Content */}
            <div className="flex-1 flex flex-col overflow-hidden px-6 py-4">
              {/* AI Analysis Header */}
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-bold text-slate-900">AI Analysis</span>
                </div>
                {phase === "reasoning" && visibleSteps < reasoningSteps.length && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full"
                    />
                    <span className="text-[10px] font-bold text-blue-600">Analyzing...</span>
                  </div>
                )}
                {(phase === "recommendation" || phase === "complete") && (
                  <Badge className="bg-blue-600 text-white hover:bg-blue-700 border-0 text-[10px]">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Analysis Complete
                  </Badge>
                )}
              </div>

              {/* Recommendation Card (appears after reasoning) */}
              <AnimatePresence>
                {showRecommendation && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    transition={{ duration: 0.4 }}
                    className="mb-3 flex-shrink-0"
                  >
                    <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                          1 Recommended Action
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-bold text-slate-900">
                              APPEAL CLAIM: Appeal CO-50 Denial — D4341 (SRP) x2 — $1,240
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Clinical evidence supports medical necessity. Perio Stage III with pockets ≥4mm and significant BOP.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <AnimatePresence>
                        {!showComplete ? (
                          <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-md hover:bg-blue-700 transition-colors"
                          >
                            Review & Submit Appeal
                            <ArrowRight className="w-3.5 h-3.5" />
                          </motion.button>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-bold rounded-lg"
                          >
                            <CheckCircle className="w-4 h-4 text-blue-600" />
                            Appeal Generated & Submitted to Delta Dental
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Reasoning Steps */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-xs font-bold text-slate-700">
                      {visibleSteps} of {reasoningSteps.length} steps
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400">View Full Analysis</span>
                </div>

                <div ref={stepsRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
                  {reasoningSteps.slice(0, visibleSteps).map((step, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-start gap-2 py-1"
                    >
                      {step.status === "done" ? (
                        <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                      ) : step.status === "alert" ? (
                        <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      ) : step.status === "search" ? (
                        <Search className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                      ) : step.status === "challenge" ? (
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-xs text-slate-600 leading-relaxed">{step.text}</span>
                    </motion.div>
                  ))}
                  {phase === "reasoning" && visibleSteps < reasoningSteps.length && (
                    <motion.div
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="flex items-center gap-2 py-1 px-1"
                    >
                      <div className="w-3.5 h-3.5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
                      <span className="text-xs text-blue-600 font-medium">Reasoning...</span>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="h-10 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-6 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-[10px] font-bold text-blue-700">AI-powered denial analysis with multi-step reasoning</span>
              </div>
              <span className="text-[10px] font-bold text-blue-600">Sarah — Denial Management</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DenialManagementSlide;
